import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ComposerFormValues } from '../schema';
import type { ForwardAttachment } from '../types';
import { EmailAttachments } from '~/components/email-attachments';

/**
 * The original's attachments, as removable tiles. Dropping one is the way back
 * under the sending limits, which is what makes the server's refusal to
 * silently truncate an actionable error rather than a wall.
 */
export function ForwardAttachments({
  attachments,
}: {
  attachments: Array<ForwardAttachment>;
}) {
  const { t } = useTranslation('mailbox');
  const { control, formState, getValues, setValue } =
    useFormContext<ComposerFormValues>();
  const keptIds = useWatch({ control, name: 'keptForwardAttachmentIds' });
  const kept = attachments.filter((attachment) =>
    keptIds.includes(attachment.storageId),
  );

  function removeAttachment(index: number) {
    const dropped = kept[index];

    setValue(
      'keptForwardAttachmentIds',
      getValues('keptForwardAttachmentIds').filter(
        (id) => id !== dropped?.storageId,
      ),
      { shouldDirty: true, shouldValidate: true },
    );
  }

  if (kept.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 flex shrink-0 flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">
        {t('Forwarded attachments')}
      </span>
      <EmailAttachments
        attachments={kept}
        onRemove={removeAttachment}
        removeDisabled={formState.isSubmitting}
      />
    </div>
  );
}
